"use client";

import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  Phone,
  Mail,
  Upload,
  Pencil,
  Check,
  X,
  Building2,
  User,
  Image as ImageIcon,
  Sparkles,
  Loader2,
  Star,
  Crop,
  IdCard
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import axiosInstance from "@/config/axiosInstance";
import { endpoints } from "@/config/apiEndpoints";
import { ImageCropModal } from "@/components/ui/ImageCropModal";

// ─── Helper: route already-saved (non data:/blob:) S3 image URLs through the
// backend image proxy before feeding them to the crop modal's <canvas>, so the
// export doesn't fail with a cross-origin "tainted canvas" error. Copied
// verbatim from the property templates' own getEditableImageUrl helper.
const getEditableImageUrl = (url: string): string => {
  if (!url || url.startsWith("data:") || url.startsWith("blob:")) return url;
  try {
    const parsed = new URL(url, window.location.origin);
    const needsProxy =
      parsed.hostname.includes("amazonaws.com") ||
      parsed.hostname.includes("mylistiq-production.s3");
    if (!needsProxy) return url;
    return `${endpoints.image_proxy}?url=${encodeURIComponent(url)}`;
  } catch {
    return url;
  }
};

// ─── Helper: convert the crop modal's output data URL into a File, the same
// way a freshly-picked photo already becomes a File from a file input.
const dataUrlToFile = (dataUrl: string, filename: string): File => {
  const arr = dataUrl.split(",");
  const mimeMatch = arr[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : "image/png";
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
};

// =========================================================================
// TYPES
// =========================================================================
interface ServiceData {
  id?: string | number;
  title?: string;
  headline?: string;
  description?: string;
  service_type?: string;
  serviceType?: string;
  heroImage?: string;
  hero_image?: string;
  hero_image_url?: string;
  image?: string;
  secondaryImage?: string;
  photos?: string[];
  photos_urls?: string[];
  features?: (string | { name?: string; feature_name?: string; id?: number })[];
  phone?: string;
  email?: string;
  website?: string;
  address?: string;
  agentName?: string;
  agentPhone?: string;
  agentEmail?: string;
  agentAvatar?: string;
  companyName?: string;
  company?: string;
  profileImage?: string;
  companyImage?: string;
  companyLogo?: string;
  subject?: string;
  [key: string]: any;

}

interface UserData {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone_number?: string;
  avatar?: string;
  company?: string;
  company_logo?: string;
  license_number?: string;
}

interface ServiceTemplate3Props {
  serviceData?: ServiceData;
  data?: ServiceData;
  onDirtyChange?: (dirty: boolean) => void;
  onChange?: (updatedFields: Record<string, any>) => void;
  readOnly?: boolean;
  isPreview?: boolean;
}

// =========================================================================
// CONSTANTS
// =========================================================================
const MAX_FEATURES = 10;

const DEFAULT_FEATURES = [
  { id: 1, feature_name: "Office & Commercial Cleaning" },
  { id: 2, feature_name: "Residential Deep Cleaning" },
  { id: 3, feature_name: "Window & Glass Cleaning" },
  { id: 4, feature_name: "Carpet & Upholstery Cleaning" },
  { id: 5, feature_name: "Move-in / Move-out Cleaning" },
  { id: 6, feature_name: "Post-Construction Cleaning" },
];

// =========================================================================
// HELPER FUNCTIONS
// =========================================================================
const getHeroImageUrl = (data: ServiceData | undefined): string => {
  if (!data) return "";
  return data.heroImage || data.hero_image_url || data.hero_image || data.image || "";
};

const getSecondaryImageUrl = (data: ServiceData | undefined): string => {
  if (!data) return "";
  if (data.secondaryImage && data.secondaryImage.length > 0) return data.secondaryImage;
  if (data.photos_urls && Array.isArray(data.photos_urls) && data.photos_urls.length > 0) return data.photos_urls[0];
  if (data.photos && Array.isArray(data.photos) && data.photos.length > 0) {
    const photoUrl = data.photos[0];
    if (photoUrl.startsWith("http") || photoUrl.startsWith("data:")) return photoUrl;
    const baseUrl = axiosInstance.defaults.baseURL || "http://localhost:8000";
    return `${baseUrl}${photoUrl.startsWith("/") ? "" : "/"}${photoUrl}`;
  }
  return "";
};

const normalizeFeatures = (features: any): { id?: number; feature_name: string }[] => {
  if (!features || !Array.isArray(features)) return [];
  return features
    .map((f, index) => {
      if (typeof f === "string") return { id: index, feature_name: f.trim() };
      if (typeof f === "object" && f !== null) return { id: f.id ?? index, feature_name: (f.name || f.feature_name || "").trim() };
      return { id: index, feature_name: "" };
    })
    .filter((f) => f.feature_name.length > 0);
};

type FeatureRow = {
  id: string;
  feature_name: string;
};

const createFeatureId = () =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const areFeatureRowsEqual = (left: FeatureRow[], right: { feature_name: string }[]) =>
  left.length === right.length &&
  left.every((item, index) => item.feature_name.trim() === right[index]?.feature_name.trim());

const mapFeatureRows = (features: any, previous: FeatureRow[] = []): FeatureRow[] => {
  const normalized = normalizeFeatures(features).map((feature) => ({
    feature_name: feature.feature_name,
  }));

  if (normalized.length === 0) return previous;
  if (areFeatureRowsEqual(previous, normalized)) return previous;

  const mapped = normalized.map((feature, index) => ({
    id: previous[index]?.id ?? createFeatureId(),
    feature_name: feature.feature_name,
  }));

  const trailingEmptyRows = previous
    .slice(normalized.length)
    .filter((row) => !row.feature_name.trim());

  return [...mapped, ...trailingEmptyRows];
};

const mapDefaultFeatureRows = (): FeatureRow[] =>
  DEFAULT_FEATURES.map((feature) => ({
    id: `default-${feature.id}`,
    feature_name: feature.feature_name,
  }));

// =========================================================================
// EDITABLE TEXT COMPONENT
// =========================================================================
const EditableText = ({
  value,
  defaultValue,
  onChange,
  readOnly,
  className = "",
  as = "span",
  multiline = false,
  placeholder = "Click to edit...",
  inputClassName = "",
}: {
  value?: string;
  defaultValue: string;
  onChange: (value: string) => void;
  readOnly: boolean;
  className?: string;
  as?: "span" | "h1" | "h2" | "h3" | "p";
  multiline?: boolean;
  placeholder?: string;
  inputClassName?: string;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value || "");
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => { setTempValue(value || ""); }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const displayValue = value?.trim() || defaultValue;

  const handleSave = () => {
    onChange(tempValue.trim());
    setIsEditing(false);
  };

  const handleCancel = () => {
    setTempValue(value || "");
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !multiline) { e.preventDefault(); handleSave(); }
    if (e.key === "Escape") handleCancel();
  };

  if (readOnly) {
    const Tag = as as keyof JSX.IntrinsicElements;
    return <Tag className={className}>{displayValue}</Tag>;
  }

  if (isEditing) {
    return (
      <div className="flex items-start gap-1.5 w-full min-w-0">
        {multiline ? (
          <textarea
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className={`flex-1 min-w-0 bg-white border-2 border-cyan-500 rounded-lg px-2 py-1.5 text-gray-800 focus:outline-none focus:ring-2 focus:ring-cyan-300 resize-y min-h-[80px] shadow-sm text-sm md:text-base ${inputClassName}`}
            placeholder={placeholder}
            rows={4}
          />
        ) : (
          <input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className={`flex-1 min-w-0 bg-white border-2 border-cyan-500 rounded-lg px-2 py-1.5 text-gray-800 focus:outline-none focus:ring-2 focus:ring-cyan-300 shadow-sm text-sm md:text-base ${inputClassName}`}
            placeholder={placeholder}
          />
        )}
        <button onClick={handleSave} className="p-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex-shrink-0">
          <Check className="w-3.5 h-3.5" />
        </button>
        <button onClick={handleCancel} className="p-1.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors flex-shrink-0">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  const Tag = as as keyof JSX.IntrinsicElements;
  return (
    <div className="group relative block w-full min-w-0 max-w-full" onClick={() => setIsEditing(true)} title="Click to edit">
      <Tag className={`${className} border-b-2 border-dashed border-transparent group-hover:border-cyan-400 transition-all duration-200 pb-0.5 break-words`}>
        {displayValue || placeholder}
      </Tag>
      <Pencil className="absolute -right-5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity text-cyan-600" />
    </div>
  );
};

// =========================================================================
// MAIN COMPONENT
// =========================================================================
export default function ServiceTemplate3({
  serviceData: propServiceData,
  data: propData,
  onDirtyChange,
  onChange,
  readOnly = true,
  isPreview = false,
}: ServiceTemplate3Props) {
  const { toast } = useToast();
  const incomingData = propServiceData || propData || {};

  const [localEdits, setLocalEdits] = useState<Partial<ServiceData>>({});
  const [userData, setUserData] = useState<UserData>({});
  const [loading, setLoading] = useState(true);
  const [savingField, setSavingField] = useState<string | null>(null);
  const [isSavingImage, setIsSavingImage] = useState(false);
  const [features, setFeatures] = useState<FeatureRow[]>([]);
  const [boxHeroRes, setBoxHeroRes] = useState<string | null>("380x380");
  const [boxSecondaryRes, setBoxSecondaryRes] = useState<string | null>("380x380");
  const heroContainerRef = useRef<HTMLDivElement | null>(null);
  const secondaryContainerRef = useRef<HTMLDivElement | null>(null);

  const incomingSyncKey = useMemo(
    () =>
      [
        incomingData.id,
        incomingData.title,
        incomingData.description,
        getHeroImageUrl(incomingData),
        getSecondaryImageUrl(incomingData),
        JSON.stringify(incomingData.features ?? []),
      ].join("::"),
    [incomingData]
  );

  const serviceData = useMemo(() => {
    const base = propServiceData || propData || {};
    if (readOnly) return base;
    return { ...base, ...localEdits };
  }, [readOnly, incomingSyncKey, localEdits, propServiceData, propData]);
  const heroImageUrl = useMemo(() => getHeroImageUrl(serviceData), [serviceData]);
  const secondaryImageUrl = useMemo(() => getSecondaryImageUrl(serviceData), [serviceData]);

  useEffect(() => {
    const measure = () => {
      if (heroContainerRef.current) {
        setBoxHeroRes(
          `${Math.round(heroContainerRef.current.clientWidth)}x${Math.round(
            heroContainerRef.current.clientHeight
          )}`
        );
      }
      if (secondaryContainerRef.current) {
        setBoxSecondaryRes(
          `${Math.round(secondaryContainerRef.current.clientWidth)}x${Math.round(
            secondaryContainerRef.current.clientHeight
          )}`
        );
      }
    };

    measure();
    const rafId = window.requestAnimationFrame(measure);
    window.addEventListener("resize", measure);

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(measure);
      if (heroContainerRef.current) resizeObserver.observe(heroContainerRef.current);
      if (secondaryContainerRef.current) resizeObserver.observe(secondaryContainerRef.current);
    }

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", measure);
      resizeObserver?.disconnect();
    };
  }, []);

  useEffect(() => {
    setFeatures((prev) => {
      const mapped = mapFeatureRows(serviceData.features, prev);
      if (mapped.length > 0) return mapped;
      return prev.length === 0 ? mapDefaultFeatureRows() : prev;
    });
  }, [serviceData.features]);

  const prevIdRef = useRef<string | number | undefined>();
  useEffect(() => {
    if (prevIdRef.current !== incomingData.id) {
      setLocalEdits({});
      setFeatures((prev) => {
        const mapped = mapFeatureRows(incomingData.features, prev);
        return mapped.length > 0 ? mapped : mapDefaultFeatureRows();
      });
      prevIdRef.current = incomingData.id;
    }
  }, [incomingData.id, incomingData.features]);

  const prevReadOnlyRef = useRef(readOnly);
  useEffect(() => {
    if (readOnly && !prevReadOnlyRef.current) {
      setLocalEdits({});
    }
    prevReadOnlyRef.current = readOnly;
  }, [readOnly]);

  useEffect(() => {
    if (readOnly) {
      setFeatures((prev) => {
        const mapped = mapFeatureRows(incomingData.features, prev);
        return mapped.length > 0 ? mapped : prev;
      });
    }
  }, [readOnly, incomingSyncKey, incomingData.features]);

  useEffect(() => {
    let isMounted = true;
    const fetchUserData = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get(endpoints.me);
        const data = response.data;
        if (!isMounted) return;
        setUserData(data);
        setLocalEdits((prev) => ({
          ...prev,
          profileImage: prev.profileImage || incomingData.profileImage || data.avatar || "",
          companyImage: prev.companyImage || incomingData.companyImage || data.company_logo || "",
          agentName: prev.agentName || incomingData.agentName || `${data.first_name || ""} ${data.last_name || ""}`.trim(),
          agentPhone: prev.agentPhone || incomingData.agentPhone || data.phone_number || "",
          agentEmail: prev.agentEmail || incomingData.agentEmail || data.email || "",
          company: prev.company || prev.companyName || incomingData.company || incomingData.companyName || data.company || "",
        }));
      } catch (err) {
        console.error("Failed to load user data:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchUserData();
    return () => { isMounted = false; };
  }, []);

  const handleChange = useCallback((field: keyof ServiceData, value: any) => {
    setLocalEdits((prev) => ({ ...prev, [field]: value }));
    onChange?.({ [field]: value });
    onDirtyChange?.(true);
  }, [onChange, onDirtyChange]);

  const saveFieldToBackend = useCallback(async (field: string, value: any) => {
    if (!serviceData.id) return;
    setSavingField(field);
    try {
      const formData = new FormData();
      if (field === "features" && Array.isArray(value)) {
        const featureNames = value
          .map((f: any) => String(f?.feature_name ?? f ?? "").trim())
          .filter((f: string) => f.length > 0);
        formData.append("features", JSON.stringify(featureNames));
      } else {
        formData.append(field, String(value));
      }
      await axiosInstance.patch(`${endpoints.service_listings}${serviceData.id}/`, formData);
      const { data } = await axiosInstance.get(`${endpoints.service_listings}${serviceData.id}/`);
      const heroRaw = data.hero_image_url || data.hero_image || "";
      const heroFull = heroRaw
        ? (heroRaw.startsWith("http") ? heroRaw : `${axiosInstance.defaults.baseURL || ""}${heroRaw.startsWith("/") ? "" : "/"}${heroRaw}`)
        : "";
      const photos = (Array.isArray(data.photos) ? data.photos : []).map((p: string) =>
        p?.startsWith("http") ? p : `${axiosInstance.defaults.baseURL || ""}${p?.startsWith("/") ? "" : "/"}${p}`
      );
      const synced: Record<string, any> = {
        title: data.title,
        description: data.description,
        service_type: data.service_type,
        serviceType: data.service_type,
        phone: data.phone,
        email: data.email,
        website: data.website,
        address: data.address,
        heroImage: heroFull,
        hero_image_url: heroFull,
        image: heroFull,
        photos,
        photos_urls: photos,
        features: data.features,
      };
      setLocalEdits((prev) => ({ ...prev, ...synced }));
      onChange?.(synced);
    } catch (error) {
      console.error(`Failed to save ${field}:`, error);
    } finally {
      setSavingField(null);
    }
  }, [serviceData.id, onChange]);

  const handleHeroImageUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>, options?: { captureOriginal?: boolean }) => {
    // captureOriginal defaults to true: a genuinely new photo pick becomes
    // its own untouched "original" so Edit Image can always re-crop from
    // the full source later. The Edit Image crop-confirm path reuses this
    // same handler via a synthetic file-input event and passes
    // captureOriginal: false, so re-cropping an already-cropped photo
    // doesn't overwrite the true original with the crop.
    const captureOriginal = options?.captureOriginal ?? true;
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      if (boxHeroRes) {
        const img = new Image();
        img.onload = () => {
          const parts = boxHeroRes.split(/×|x/).map((s) => Number(s.trim()));
          if (parts.length === 2) {
            const [rw, rh] = parts;
            const mismatch = img.naturalWidth !== rw || img.naturalHeight !== rh;
            if (mismatch) {
              toast({
                title: "Resolution Suggestion",
                description: `Uploaded image is ${img.naturalWidth}x${img.naturalHeight}. Recommended: ${boxHeroRes}. ...`,
              });
            }
          }
        };
        img.src = dataUrl;
      }
      handleChange("heroImage", dataUrl);
      handleChange("hero_image_url", dataUrl);
      if (captureOriginal) setOriginalHeroImage(dataUrl);
    };
    reader.readAsDataURL(file);
    if (serviceData.id) {
      setIsSavingImage(true);
      const formData = new FormData();
      formData.append("hero_image", file);
      try {
        const response = await axiosInstance.patch(`${endpoints.service_listings}${serviceData.id}/`, formData);
        if (response.data?.hero_image_url) {
          handleChange("heroImage", response.data.hero_image_url);
          handleChange("hero_image_url", response.data.hero_image_url);
          if (captureOriginal) {
            setOriginalHeroImage(response.data.hero_image_url);
            axiosInstance
              .patch(`${endpoints.service_listings}${serviceData.id}/`, {
                original_hero_image: response.data.hero_image_url,
              })
              .catch((err) => {
                console.error("Failed to seed original hero image:", err);
              });
          }
        }
      } catch (err) {
        console.error("Failed to upload hero image:", err);
      } finally {
        setIsSavingImage(true);
      }
    }
    e.target.value = "";
  }, [serviceData.id, handleChange, boxHeroRes, toast]);

  const handleSecondaryImageUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>, options?: { captureOriginal?: boolean }) => {
    // See handleHeroImageUpload above for why captureOriginal defaults to
    // true but is passed as false from the Edit Image crop-confirm path.
    const captureOriginal = options?.captureOriginal ?? true;
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      if (boxSecondaryRes) {
        const img = new Image();
        img.onload = () => {
          const parts = boxSecondaryRes.split(/×|x/).map((s) => Number(s.trim()));
          if (parts.length === 2) {
            const [rw, rh] = parts;
            toast({
              title: "Resolution Suggestion",
              description: `Uploaded image is ${img.naturalWidth}x${img.naturalHeight}. Recommended: ${boxSecondaryRes}. If possible, upload this resolution for best results.`,
            });
          }
        };
        img.src = dataUrl;
      }
      const newPhotos = [dataUrl, ...(serviceData.photos || []).slice(1)];
      const newPhotosUrls = [dataUrl, ...(serviceData.photos_urls || []).slice(1)];
      setLocalEdits((prev) => ({ ...prev, secondaryImage: dataUrl, photos: newPhotos, photos_urls: newPhotosUrls }));
      onChange?.({ secondaryImage: dataUrl, photos: newPhotos, photos_urls: newPhotosUrls });
      onDirtyChange?.(true);
      if (captureOriginal) setOriginalSecondaryImage(dataUrl);
    };
    reader.readAsDataURL(file);
    if (serviceData.id) {
      setIsSavingImage(true);

      const formData = new FormData();
      formData.append("photo_1", file);
      try {
        const response = await axiosInstance.patch(
          `${endpoints.service_listings}${serviceData.id}/`,
          formData,
          { headers: { "Content-Type": undefined as any }, transformRequest: [(data) => data] }
        );
        const serverPhotosUrls = response.data?.photos_urls || [];
        const serverPhotos = response.data?.photos || serverPhotosUrls;
        const serverUrl = serverPhotosUrls[0] || serverPhotos[0] || null;
        if (serverUrl) {
          setLocalEdits((prev) => ({ ...prev, secondaryImage: serverUrl, photos: serverPhotos, photos_urls: serverPhotosUrls }));
          onChange?.({ secondaryImage: serverUrl, photos: serverPhotos, photos_urls: serverPhotosUrls });
          if (captureOriginal) {
            setOriginalSecondaryImage(serverUrl);
            axiosInstance
              .patch(`${endpoints.service_listings}${serviceData.id}/`, {
                original_photos: [serverUrl],
              })
              .catch((err) => {
                console.error("Failed to seed original secondary image:", err);
              });
          }
        }
      } catch (err) {
        console.error("Failed to upload secondary image:", err);
      } finally {
        setIsSavingImage(false);
      }
    }
    e.target.value = "";
  }, [serviceData.id, serviceData.photos, serviceData.photos_urls, onChange, onDirtyChange, boxSecondaryRes, toast]);

  // =========================================================================
  // EDIT IMAGE (crop/zoom the hero + secondary photo, non-destructively)
  // =========================================================================
  const EDIT_CROP_W = 380;
  const EDIT_CROP_H = 380; // matches the square hero/secondary box

  const [cropModal, setCropModal] = useState<{ open: boolean; imageUrl: string }>({
    open: false,
    imageUrl: "",
  });
  const [originalHeroImage, setOriginalHeroImage] = useState<string>("");

  const openEditImage = async () => {
    if (readOnly || !heroImageUrl) return;

    let originalSource = originalHeroImage || (serviceData as any)?.original_hero_image || "";

    if (!originalSource && serviceData.id) {
      try {
        const res = await axiosInstance.get(`${endpoints.service_listings}${serviceData.id}/`);
        originalSource = res?.data?.original_hero_image || "";
      } catch (err) {
        console.error("Failed to check for existing original hero image:", err);
      }
    }

    if (!originalSource) {
      originalSource = heroImageUrl;
      if (serviceData.id && !heroImageUrl.startsWith("data:")) {
        axiosInstance
          .patch(`${endpoints.service_listings}${serviceData.id}/`, {
            original_hero_image: heroImageUrl,
          })
          .catch((err) => {
            console.error("Failed to seed original hero image:", err);
          });
      }
    }

    setOriginalHeroImage(originalSource);
    setCropModal({ open: true, imageUrl: getEditableImageUrl(originalSource) });
  };

  const handleEditImageConfirm = (croppedDataUrl: string) => {
    // Reuse the existing, unmodified hero upload handler by feeding it a
    // synthetic file-input change event, so the crop-confirm path saves
    // exactly like a normal re-upload.
    const file = dataUrlToFile(croppedDataUrl, `edited-hero-${Date.now()}.png`);
    const syntheticEvent = {
      target: { files: [file], value: "" },
    } as unknown as React.ChangeEvent<HTMLInputElement>;
    handleHeroImageUpload(syntheticEvent, { captureOriginal: false });
    setCropModal({ open: false, imageUrl: "" });
  };

  const handleEditImageCancel = () => {
    setCropModal({ open: false, imageUrl: "" });
  };

  const [secondaryCropModal, setSecondaryCropModal] = useState<{ open: boolean; imageUrl: string }>({
    open: false,
    imageUrl: "",
  });
  const [originalSecondaryImage, setOriginalSecondaryImage] = useState<string>("");

  const openEditSecondaryImage = async () => {
    if (readOnly || !secondaryImageUrl) return;

    let originalSource =
      originalSecondaryImage || ((serviceData as any)?.original_photos?.[0] ?? "");

    if (!originalSource && serviceData.id) {
      try {
        const res = await axiosInstance.get(`${endpoints.service_listings}${serviceData.id}/`);
        originalSource = res?.data?.original_photos?.[0] || "";
      } catch (err) {
        console.error("Failed to check for existing original secondary image:", err);
      }
    }

    if (!originalSource) {
      originalSource = secondaryImageUrl;
      if (serviceData.id && !secondaryImageUrl.startsWith("data:")) {
        axiosInstance
          .patch(`${endpoints.service_listings}${serviceData.id}/`, {
            original_photos: [secondaryImageUrl],
          })
          .catch((err) => {
            console.error("Failed to seed original secondary image:", err);
          });
      }
    }

    setOriginalSecondaryImage(originalSource);
    setSecondaryCropModal({ open: true, imageUrl: getEditableImageUrl(originalSource) });
  };

  const handleSecondaryEditImageConfirm = (croppedDataUrl: string) => {
    const file = dataUrlToFile(croppedDataUrl, `edited-secondary-${Date.now()}.png`);
    const syntheticEvent = {
      target: { files: [file], value: "" },
    } as unknown as React.ChangeEvent<HTMLInputElement>;
    handleSecondaryImageUpload(syntheticEvent, { captureOriginal: false });
    setSecondaryCropModal({ open: false, imageUrl: "" });
  };

  const handleSecondaryEditImageCancel = () => {
    setSecondaryCropModal({ open: false, imageUrl: "" });
  };

  const addFeature = useCallback(() => {
    if (readOnly || features.length >= MAX_FEATURES) return;
    setFeatures((prev) => [...prev, { id: createFeatureId(), feature_name: "" }]);
    onDirtyChange?.(true);
  }, [readOnly, features.length, onDirtyChange]);

  const handleFeatureChange = useCallback((index: number, value: string) => {
    setFeatures((prev) => { const updated = [...prev]; updated[index] = { ...updated[index], feature_name: value }; return updated; });
    onDirtyChange?.(true);
  }, [onDirtyChange]);

  const handleFeatureBlur = useCallback(() => {
    const featureNames = features
      .map((f) => String(f?.feature_name ?? f ?? "").trim())
      .filter((f) => f.length > 0);
    handleChange("features", featureNames);
    void saveFieldToBackend("features", features);
  }, [features, handleChange, saveFieldToBackend]);

  const handleFeatureDelete = useCallback((index: number) => {
    if (features.length <= 1) return;
    const updated = features.filter((_, i) => i !== index);
    const featureNames = updated
      .map((f) => String(f?.feature_name ?? f ?? "").trim())
      .filter((f) => f.length > 0);
    setFeatures(updated);
    handleChange("features", featureNames);
    void saveFieldToBackend("features", updated);
  }, [features.length, handleChange, saveFieldToBackend]);

  // Computed display values
  const title = serviceData.title?.trim() || "Professional Cleaning Services";
  const description = serviceData.description?.trim() || "We provide reliable, high-quality cleaning for homes and businesses.";
  const agentFullName = serviceData.agentName || localEdits.agentName || incomingData.agentName || (userData?.first_name ? `${userData.first_name} ${userData.last_name || ""}`.trim() : "") || "Your Name";
  const displayPhone = userData?.phone_number || localEdits.agentPhone || serviceData.agentPhone || localEdits.phone || serviceData.phone || incomingData.agentPhone || incomingData.phone || "";
  const displayEmail = userData?.email || localEdits.agentEmail || serviceData.agentEmail || localEdits.email || serviceData.email || incomingData.agentEmail || incomingData.email || "";
  const displayCompanyName = userData?.company || localEdits.company || serviceData.company || localEdits.companyName || serviceData.companyName || incomingData.company || incomingData.companyName || "Your Company";
  const displayAvatar = serviceData.profileImage || localEdits.profileImage || serviceData.agentAvatar || incomingData.profileImage || incomingData.agentAvatar || userData?.avatar || "";
  const displayCompanyLogo = serviceData.companyImage || localEdits.companyImage || serviceData.companyLogo || incomingData.companyImage || incomingData.companyLogo || userData?.company_logo || "";
  const editablePhoneValue = serviceData.phone || localEdits.phone || serviceData.agentPhone || localEdits.agentPhone || "";
  const editableEmailValue = serviceData.email || localEdits.email || serviceData.agentEmail || localEdits.agentEmail || "";

  if (loading && !isPreview) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-gradient-to-b from-sky-50 to-white">
        <div className="flex flex-col items-center gap-3">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-sky-500 border-t-transparent" />
          <p className="text-sky-700 font-medium text-sm">Loading template...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto w-full" style={{ WebkitOverflowScrolling: "touch", transform: "rotateX(180deg)" }}>
      <div className="bg-gradient-to-b from-[#e8f4fc] via-[#f0f9ff] to-white shadow-none rounded-2xl sm:rounded-3xl overflow-hidden relative inline-block align-top" style={{ minWidth: "840px", maxWidth: "840px", transform: "rotateX(180deg)" }}>

        {/* Saving Indicator */}
        {isSavingImage && (
          <div className="fixed top-4 right-4 z-50 bg-white/90 backdrop-blur-sm px-3 py-2 rounded-lg shadow-lg flex items-center gap-2 text-sm">
            <Loader2 className="w-4 h-4 animate-spin text-sky-600 flex-shrink-0" />
            <span className="text-gray-700">Updating images...</span>
          </div>
        )}

        {/* ── HEADER ── */}
        <div className="bg-gradient-to-r from-[#0ea5e9] to-[#0284c7] px-4 sm:px-6 md:px-10 py-4 sm:py-5 md:py-6 text-white">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
              {/* <Sparkles className="h-5 w-5 sm:h-6 sm:w-6" /> */}
              <img
                src="https://mylistiq-production.s3.us-east-1.amazonaws.com/static/image/sprakle_white_bold.png"
                width="25"
                style={{ display: "block", border: "0" }}
                alt=""
              ></img>
            </div>
            <div className="min-w-0 flex-1">
              <EditableText
                value={serviceData.companyName || serviceData.company}
                defaultValue={displayCompanyName}
                onChange={(v) => { handleChange("companyName", v); saveFieldToBackend("companyName", v); }}
                readOnly={readOnly}
                as="h2"
                className="text-lg sm:text-2xl md:text-3xl font-bold truncate block"
                inputClassName="text-lg sm:text-2xl"
              />
            </div>
          </div>
        </div>

        {/* ── TWO PHOTOS ── */}
        <div className="px-3 py-4 md:px-4 md:py-5">
          <div className="grid grid-cols-2 gap-2 md:gap-3 justify-items-center">

            {/* Hero Image */}
            <div ref={heroContainerRef} className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-sky-50 to-sky-100 border-2 border-sky-200 group shadow-md"
              style={{
                width: "380px",
                maxWidth: "380px",
                minWidth: "380px",
                height: "380px",
                maxHeight: "380px",
                minHeight: "380px",
              }}>
              {!readOnly && boxHeroRes && (
                <div className="absolute bottom-3 left-3 z-30 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="bg-black/80 text-white text-xs rounded-xl px-3 py-2 backdrop-blur-sm border border-white/10 max-w-[320px]">
                    <div className="leading-snug">
                      <div className="font-semibold">
                        Required image resolution: {boxHeroRes}
                      </div>
                      <div className="mt-1 text-[11px]">
                        Please upload exactly this resolution.
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {heroImageUrl ? (
                <>
                  <img
                    src={heroImageUrl}
                    alt="Main service view"
                    className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                  />
                  {!readOnly && (
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <label className="px-4 py-2.5 sm:px-6 sm:py-3 bg-white/90 rounded-xl font-medium shadow-lg hover:bg-white transition-colors flex items-center gap-2 cursor-pointer text-sm sm:text-base">
                        <Upload className="w-4 h-4" />
                        Change Photo
                        <input type="file" accept="image/*" className="hidden" onChange={handleHeroImageUpload} />
                      </label>
                      <button
                        onClick={openEditImage}
                        className="px-4 py-2.5 sm:px-6 sm:py-3 bg-white/90 rounded-xl font-medium shadow-lg hover:bg-white transition-colors flex items-center gap-2 cursor-pointer text-sm sm:text-base"
                      >
                        <Crop className="w-4 h-4" />
                        Edit
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <label className="absolute inset-0 flex flex-col items-center justify-center text-sky-600 cursor-pointer">
                  <ImageIcon className="h-20 w-20 mb-4" />
                  <p className="text-2xl font-bold">Main Photo</p>
                  <p className="text-sm mt-2 opacity-80">{readOnly ? "No image" : "Click to upload"}</p>
                  {!readOnly && <input type="file" accept="image/*" className="hidden" onChange={handleHeroImageUpload} />}
                </label>
              )}
              {!readOnly && (
                <ImageCropModal
                  open={cropModal.open}
                  imageUrl={cropModal.imageUrl}
                  title="Adjust Main Photo"
                  outputSize={700}
                  cropW={EDIT_CROP_W}
                  cropH={EDIT_CROP_H}
                  onConfirm={handleEditImageConfirm}
                  onCancel={handleEditImageCancel}
                />
              )}
            </div>

            {/* Secondary Image */}
            <div ref={secondaryContainerRef} className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-sky-50 to-sky-100 border-2 border-sky-200 group shadow-md"
              style={{
                width: "380px",
                maxWidth: "380px",
                minWidth: "380px",
                height: "380px",
                maxHeight: "380px",
                minHeight: "380px",
              }}>
              {!readOnly && boxSecondaryRes && (
                <div className="absolute bottom-3 left-3 z-30 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="bg-black/80 text-white text-xs rounded-xl px-3 py-2 backdrop-blur-sm border border-white/10 max-w-[320px]">
                    <div className="leading-snug">
                      <div className="font-semibold">
                        Required image resolution: {boxSecondaryRes}
                      </div>
                      <div className="mt-1 text-[11px]">
                        Please upload exactly this resolution.
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {secondaryImageUrl ? (
                <>
                  <img
                    src={secondaryImageUrl}
                    alt="Secondary view"
                    className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                  />
                  {!readOnly && (
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <label className="px-4 py-2.5 sm:px-6 sm:py-3 bg-white/90 rounded-xl font-medium shadow-lg hover:bg-white transition-colors flex items-center gap-2 cursor-pointer text-sm sm:text-base">
                        <Upload className="w-4 h-4" />
                        Change Photo
                        <input type="file" accept="image/*" className="hidden" onChange={handleSecondaryImageUpload} />
                      </label>
                      <button
                        onClick={openEditSecondaryImage}
                        className="px-4 py-2.5 sm:px-6 sm:py-3 bg-white/90 rounded-xl font-medium shadow-lg hover:bg-white transition-colors flex items-center gap-2 cursor-pointer text-sm sm:text-base"
                      >
                        <Crop className="w-4 h-4" />
                        Edit
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <label className="absolute inset-0 flex flex-col items-center justify-center text-sky-600 cursor-pointer">
                  <ImageIcon className="h-20 w-20 mb-4" />
                  <p className="text-2xl font-bold">Secondary Photo</p>
                  {!readOnly && <input type="file" accept="image/*" className="hidden" onChange={handleSecondaryImageUpload} />}
                </label>
              )}
              {!readOnly && (
                <ImageCropModal
                  open={secondaryCropModal.open}
                  imageUrl={secondaryCropModal.imageUrl}
                  title="Adjust Secondary Photo"
                  outputSize={700}
                  cropW={EDIT_CROP_W}
                  cropH={EDIT_CROP_H}
                  onConfirm={handleSecondaryEditImageConfirm}
                  onCancel={handleSecondaryEditImageCancel}
                />
              )}
            </div>
          </div>
        </div>

        {/* ── MAIN CONTENT CARD ── */}
        <div className="px-3 sm:px-6 md:px-10 pb-6 sm:pb-8 md:pb-10">
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl p-4 sm:p-6 md:p-10 border border-sky-100">

            {/* Title */}
            <div className="text-center mb-4 sm:mb-6 md:mb-8">
              <EditableText
                key={`title-${incomingSyncKey}`}
                value={serviceData.title ?? ""}
                defaultValue={title}
                onChange={(v) => { handleChange("title", v); saveFieldToBackend("title", v); }}
                readOnly={readOnly}
                as="h1"
                className="
text-2xl sm:text-3xl md:text-4xl lg:text-5xl
font-extrabold
text-[#0c4a6e]
leading-tight

text-ellipsis
max-w-full
"
                inputClassName="text-2xl sm:text-3xl text-center"
              />
            </div>

            {/* Description */}
            <div className="text-center max-w-3xl mx-auto mb-6 sm:mb-8 md:mb-12">
              <EditableText
                key={`desc-${incomingSyncKey}`}
                value={serviceData.description ?? ""}
                defaultValue={description}
                onChange={(v) => { handleChange("description", v); saveFieldToBackend("description", v); }}
                readOnly={readOnly}
                multiline
                as="p"
                className="
text-sm sm:text-base md:text-lg
text-gray-700
leading-relaxed
overflow-hidden
text-ellipsis
break-words
max-w-full
"
                inputClassName="text-sm sm:text-base text-center"
              />
            </div>

            {/* FEATURES */}
            <div className="w-full">
              <h3 className="text-[#0c4a6e] font-bold text-base sm:text-xl md:text-2xl mb-4 sm:mb-5 md:mb-6 flex items-center justify-between flex-wrap gap-2">
                <span className="flex items-center gap-2">
                  <Star className="h-5 w-5 sm:h-6 sm:w-6 md:h-8 md:w-8 flex-shrink-0 text-[#0ea5e9]" />
                  Our Services
                </span>
                {!readOnly && (
                  <span className={`text-xs sm:text-sm font-normal px-2 py-1 rounded-full ${features.length >= MAX_FEATURES
                    ? "bg-amber-100 text-amber-700"
                    : "bg-sky-100 text-sky-600"
                    }`}>
                    {features.length}/{MAX_FEATURES}
                  </span>
                )}
              </h3>

              <div className="grid grid-cols-2 gap-2.5 sm:gap-4 md:gap-5">
                {features.map((feat, i) => (
                  <div
                    key={feat.id}
                    className="flex items-center min-h-[56px] sm:min-h-[64px] md:min-h-[72px] w-full bg-gradient-to-br from-sky-50 to-blue-50 border border-sky-200 rounded-lg sm:rounded-xl px-3 sm:px-4 md:px-5 hover:shadow-md hover:border-sky-300 transition-all group"
                  >
                    <div className="w-3 h-3 sm:w-4 sm:h-4 bg-[#0ea5e9] rounded-full mr-3 sm:mr-4 md:mr-5 flex-shrink-0" />
                    {readOnly ? (
                      <span
                        className="
  flex-1
  min-w-0
  overflow-hidden
  text-ellipsis
  whitespace-nowrap
  text-gray-800
  text-sm sm:text-base md:text-lg
  font-medium
  leading-snug
">{feat.feature_name}</span>
                    ) : (
                      <input
                        type="text"
                        value={feat.feature_name}
                        onChange={(e) => handleFeatureChange(i, e.target.value)}
                        onBlur={() => handleFeatureBlur()}
                        className="flex-1 min-w-0 bg-transparent border-none outline-none text-gray-800 text-sm sm:text-base md:text-lg placeholder-gray-400 leading-tight"
                        maxLength={30}
                        placeholder="e.g., Deep Cleaning"
                      />
                    )}
                    {!readOnly && features.length > 1 && (
                      <button
                        onClick={() => handleFeatureDelete(i)}
                        className="ml-2 sm:ml-3 text-red-400 hover:text-red-500 text-xl sm:text-2xl opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 leading-none"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}

                {!readOnly && features.length < MAX_FEATURES && (
                  <Button
                    onClick={addFeature}
                    variant="outline"
                    className="col-span-2 min-h-[56px] sm:min-h-[64px] md:min-h-[72px] w-full border-2 border-dashed border-sky-300 text-sky-600 bg-sky-50/50 hover:border-sky-400 hover:text-sky-700 hover:bg-sky-100/50 rounded-lg sm:rounded-xl text-sm sm:text-base md:text-lg font-medium transition-all"
                  >
                    + Add Service
                  </Button>
                )}

                {!readOnly && features.length >= MAX_FEATURES && (
                  <p className="col-span-full text-center text-xs sm:text-sm text-gray-500 italic">
                    Maximum of {MAX_FEATURES} services reached
                  </p>
                )}
              </div>

              {features.length === 0 && (
                <div className="text-center py-8 sm:py-12 bg-sky-50 rounded-2xl border-2 border-dashed border-sky-200 mt-4">
                  <Sparkles className="w-10 h-10 sm:w-12 sm:h-12 text-sky-300 mx-auto mb-3" />
                  <p className="text-sky-600 text-base sm:text-lg font-medium">
                    {readOnly ? "No services added" : "Click 'Add Service' to add your first service"}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* ── BOTTOM CONTACT CARD ── */}
        <div className="px-3 sm:px-5 md:px-8 pb-5 sm:pb-6 md:pb-8">
          <div className="bg-sky-50 rounded-2xl border border-sky-200 shadow-sm overflow-hidden">
            <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-4 sm:gap-5 p-3 sm:p-3 min-w-0">

              {/* ── Profile Avatar ── */}
              <div className="flex-shrink-0">
                <div className="w-[150px] h-[180px] rounded-xl overflow-hidden flex-shrink-0 flex items-center justify-center">
                  {displayAvatar ? (
                    <img src={displayAvatar} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-8 h-8 text-sky-300" />
                  )}
                </div>
              </div>

              {/* ── Contact Info ── */}
              <div className="min-w-0 overflow-hidden text-center flex flex-col items-center justify-center">

                {/* Name */}
                <h3 className="text-xl sm:text-2xl font-bold text-[#0c4a6e] mb-3 leading-tight break-words text-center">
                  {agentFullName}
                </h3>

                {/* Contact rows */}
                <div className="space-y-2 flex flex-col items-center w-full">

                  {/* Phone */}
                  {displayPhone && (
                    <div className="inline-flex items-center gap-1.5 mx-auto">
                      <Phone className="w-4 h-4 text-[#0ea5e9] flex-shrink-0" />
                      <span className="text-sm sm:text-base text-gray-700">{displayPhone}</span>
                    </div>
                  )}

                  {/* Email */}
                  {displayEmail && (
                    <div className="inline-flex items-center gap-1.5 mx-auto pl-5">
                      <Mail className="w-4 h-4 text-[#0ea5e9] flex-shrink-0" />
                      <span className="text-sm sm:text-base text-gray-700 underline">
                        {/* {displayEmail} */}
                         Click here to email Me
                        </span>
                       
                    </div>
                  )}

                  {/* Company */}
                  {displayCompanyName && (
                    <div className="inline-flex items-center gap-1.5 mx-auto">
                      <Building2 className="w-4 h-4 text-[#0ea5e9] flex-shrink-0" />
                      <span className="text-sm sm:text-base text-gray-700">{displayCompanyName}</span>
                    </div>
                  )}

                  {/* License Number */}
                  {userData?.license_number && (
                    <div className="inline-flex items-center gap-1.5 mx-auto">
                      <IdCard className="w-4 h-4 text-[#0ea5e9] flex-shrink-0" />
                      <span className="text-sm sm:text-base text-gray-700">
                        License #: {userData.license_number}
                      </span>
                    </div>
                  )}

                </div>
              </div>

              {/* Company Logo */}
              <div className="w-[180px] h-[180px] rounded-xl overflow-hidden flex-shrink-0 flex items-center justify-center">
                {displayCompanyLogo ? (
                  <img src={displayCompanyLogo} alt="Company" className="w-full h-full object-cover" />
                ) : (
                  <Building2 className="w-8 h-8 text-sky-300" />
                )}
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
